import React, { useState } from 'react';
import axios from 'axios';
import { Heart, CreditCard, Phone, Mail, CheckCircle, ExternalLink, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Donate = () => {
  const [formData, setFormData] = useState({
    donor_name: '',
    email: '',
    phone: '',
    amount: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post(`${API}/donation`, {
        ...formData,
        amount: parseFloat(formData.amount),
      });
      setShowPayment(true);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      setError('Failed to submit donation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const quickAmounts = [500, 1000, 2000, 5000, 10000];

  // Handle UPI payment
  const handleUPIPayment = () => {
    const upiId = 'rpeductionalwelfaretrusttrust@gmail.com';
    const amount = formData.amount;
    const name = 'R.P. Educational Welfare Trust';
    const note = `Donation from ${formData.donor_name}`;
    
    // Create UPI deep link
    const upiUrl = `upi://pay?pa=${upiId}&pn=${encodeURIComponent(name)}&am=${amount}&cu=INR&tn=${encodeURIComponent(note)}`;
    
    // Try to open UPI apps
    window.location.href = upiUrl;
    
    // Fallback: Show QR code or instructions after 2 seconds
    setTimeout(() => {
      if (!document.hidden) {
        alert('अगर UPI app नहीं खुला, तो कृपया manually इस UPI ID पर payment करें: rpeductionalwelfaretrusttrust@gmail.com');
      }
    }, 2000);
  };

  // Handle Phone Payment
  const handlePhonePayment = () => {
    // Open phone dialer
    window.location.href = 'tel:+919801133975';
  };

  return (
    <div className="min-h-screen">
      {/* Header Section */}
      <section className="gradient-bg text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Donate Now</h1>
          <p className="text-xl hindi-text">दान करें</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-gradient-to-r from-purple-50 via-pink-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Side - Why Donate */}
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6">Why Your Donation Matters</h2>
              <p className="text-xl text-gray-600 mb-6 hindi-text">
                आपका दान क्यों महत्वपूर्ण है
              </p>

              <div className="space-y-4 mb-8">
                {[
                  {
                    amount: '₹500',
                    impact: 'Can provide study materials to 5 students',
                    hindi: '5 छात्रों को अध्ययन सामग्री प्रदान कर सकते हैं',
                  },
                  {
                    amount: '₹1000',
                    impact: 'Can sponsor career counseling for 10 students',
                    hindi: '10 छात्रों के लिए करियर काउंसलिंग स्पॉन्सर कर सकते हैं',
                  },
                  {
                    amount: '₹5000',
                    impact: 'Can help 2 students with college admission',
                    hindi: '2 छात्रों को कॉलेज एडमिशन में मदद कर सकते हैं',
                  },
                  {
                    amount: '₹10000',
                    impact: 'Can support scholarship for 1 deserving student',
                    hindi: '1 योग्य छात्र के लिए स्कॉलरशिप समर्थन कर सकते हैं',
                  },
                ].map((item, index) => (
                  <div key={`donate-impact-${item.amount}-${index}`} className="flex items-start bg-white p-4 rounded-lg shadow-md">
                    <CheckCircle className="text-green-500 mr-3 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <p className="font-semibold text-purple-700 text-lg">{item.amount}</p>
                      <p className="text-gray-700">{item.impact}</p>
                      <p className="text-sm text-gray-600 hindi-text">{item.hindi}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tax Benefits */}
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-purple-800 mb-3">
                  Tax Benefits
                </h3>
                <p className="text-gray-700 mb-2">
                  ✔️ 80G Registration - Tax deduction benefits available
                </p>
                <p className="text-gray-700 mb-2">
                  ✔️ Section 12A Registered Trust
                </p>
                <p className="text-gray-700">
                  ✔️ NITI AAYOG Approved
                </p>
              </div>
            </div>

            {/* Right Side - Donation Form */}
            <div>
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="text-center mb-6">
                  <Heart className="mx-auto text-purple-700 mb-4" size={48} />
                  <h3 className="text-2xl font-bold text-gray-800">Make a Donation</h3>
                  <p className="text-gray-600 hindi-text">दान करें</p>
                </div>

                {showPayment ? (
                  <div className="text-center py-8">
                    <CheckCircle className="mx-auto text-green-500 mb-4" size={64} />
                    <h4 className="text-2xl font-bold text-gray-800 mb-4">Thank You!</h4>
                    <p className="text-gray-600 mb-6 hindi-text">
                      धन्यवाद! कृपया payment complete करने के लिए नीचे दिए गए options का उपयोग करें:
                    </p>
                    
                    {/* Payment Options */}
                    <div className="space-y-4">
                      <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-semibold text-gray-800 mb-3">Payment Methods:</h5>
                        <p className="text-sm text-gray-600 mb-3 hindi-text">
                          नीचे दिए गए किसी भी option पर click करें
                        </p>
                        <div className="space-y-3">
                          {/* UPI Payment - Clickable */}
                          <button
                            onClick={handleUPIPayment}
                            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-300 hover:border-green-500 hover:shadow-lg transition-all cursor-pointer"
                          >
                            <div className="text-left">
                              <p className="font-semibold text-gray-800 flex items-center">
                                <span className="bg-green-500 text-white px-2 py-1 rounded text-xs mr-2">RECOMMENDED</span>
                                UPI Payment
                              </p>
                              <p className="text-sm text-gray-600">rpeductionalwelfaretrusttrust@gmail.com</p>
                              <p className="text-xs text-green-600 mt-1">Click to pay via GPay, PhonePe, Paytm</p>
                            </div>
                            <CreditCard className="text-green-600" size={28} />
                          </button>

                          {/* Phone Payment - Clickable */}
                          <button
                            onClick={handlePhonePayment}
                            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border-2 border-blue-300 hover:border-blue-500 hover:shadow-lg transition-all cursor-pointer"
                          >
                            <div className="text-left">
                              <p className="font-semibold text-gray-800">Phone Payment</p>
                              <p className="text-sm text-gray-600">+91 9801133975</p>
                              <p className="text-xs text-blue-600 mt-1">Click to call and discuss payment</p>
                            </div>
                            <Phone className="text-blue-600" size={28} />
                          </button>

                          {/* Bank Transfer - Clickable */}
                          <button
                            onClick={() => setShowBankDetails(true)}
                            className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-2 border-purple-300 hover:border-purple-500 hover:shadow-lg transition-all cursor-pointer"
                          >
                            <div className="text-left">
                              <p className="font-semibold text-gray-800">Bank Transfer</p>
                              <p className="text-sm text-gray-600">NEFT / RTGS / IMPS</p>
                              <p className="text-xs text-purple-600 mt-1">Click to view account details</p>
                            </div>
                            <ExternalLink className="text-purple-600" size={28} />
                          </button>
                        </div>
                      </div>
                      
                      <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                        <p className="text-sm font-semibold text-gray-800">
                          Donation Amount: <span className="text-2xl text-purple-700">₹{formData.amount}</span>
                        </p>
                        <p className="text-xs text-gray-600 mt-2 hindi-text">
                          Payment करने के बाद हमें call या email करें।
                        </p>
                      </div>
                    </div>

                    {/* Bank Details Modal */}
                    {showBankDetails && (
                      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowBankDetails(false)}>
                        <div className="bg-white rounded-xl p-8 max-w-md mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-bold text-gray-800">Bank Account Details</h3>
                            <button onClick={() => setShowBankDetails(false)} className="text-gray-500 hover:text-gray-700">
                              <X size={24} />
                            </button>
                          </div>
                          
                          <div className="space-y-4">
                            <div className="bg-purple-50 p-4 rounded-lg">
                              <p className="text-sm text-gray-600">Account Name</p>
                              <p className="font-semibold text-gray-800">R.P. Educational Welfare Trust</p>
                            </div>
                            
                            <div className="bg-purple-50 p-4 rounded-lg">
                              <p className="text-sm text-gray-600">Account Number</p>
                              <p className="font-semibold text-gray-800 text-lg">Contact: +91 9801133975</p>
                            </div>
                            
                            <div className="bg-purple-50 p-4 rounded-lg">
                              <p className="text-sm text-gray-600">IFSC Code</p>
                              <p className="font-semibold text-gray-800">Contact for details</p>
                            </div>
                            
                            <div className="bg-purple-50 p-4 rounded-lg">
                              <p className="text-sm text-gray-600">Bank Name</p>
                              <p className="font-semibold text-gray-800">Contact for details</p>
                            </div>

                            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                              <p className="text-sm text-gray-700 hindi-text">
                                कृपया bank details के लिए हमें call या email करें।
                              </p>
                              <div className="mt-3 space-y-2">
                                <p className="text-sm"><strong>Phone:</strong> +91 9801133975</p>
                                <p className="text-sm"><strong>Email:</strong> rpeductionalwelfaretrusttrust@gmail.com</p>
                              </div>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => setShowBankDetails(false)}
                            className="w-full mt-6 bg-purple-600 text-white py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                          >
                            Close
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {success && (
                      <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-800 text-center">
                          Thank you for your generous donation! We'll contact you shortly.
                        </p>
                      </div>
                    )}

                    {error && (
                      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800 text-center">{error}</p>
                      </div>
                    )}

                    <form onSubmit={handleSubmit}>
                      <div className="mb-4">
                        <label className="block text-gray-700 font-medium mb-2">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="donor_name"
                          value={formData.donor_name}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="Enter your name"
                        />
                      </div>

                      <div className="mb-4">
                        <label className="block text-gray-700 font-medium mb-2">
                          Email <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="your@email.com"
                        />
                      </div>

                      <div className="mb-4">
                        <label className="block text-gray-700 font-medium mb-2">
                          Phone <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="10-digit mobile number"
                        />
                      </div>

                      <div className="mb-4">
                        <label className="block text-gray-700 font-medium mb-2">
                          Donation Amount (₹) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          name="amount"
                          value={formData.amount}
                          onChange={handleChange}
                          required
                          min="100"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="Enter amount"
                        />
                        <div className="flex flex-wrap gap-2 mt-3">
                          {quickAmounts.map((amount) => (
                            <button
                              key={amount}
                              type="button"
                              onClick={() => setFormData({ ...formData, amount: amount.toString() })}
                              className="px-4 py-2 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-lg hover:from-purple-200 hover:to-pink-200 transition-colors font-semibold"
                            >
                              ₹{amount}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="mb-6">
                        <label className="block text-gray-700 font-medium mb-2">
                          Message (Optional)
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows="3"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="Your message or dedication"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-lg font-semibold text-lg hover:from-purple-700 hover:to-pink-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center shadow-lg"
                      >
                        {loading ? (
                          <span>Processing...</span>
                        ) : (
                          <>
                            <Heart className="mr-2" size={20} />
                            Proceed to Payment
                          </>
                        )}
                      </button>
                    </form>
                  </>
                )}
              </div>

              {/* Contact Info */}
              <div className="mt-6 bg-gradient-to-r from-purple-100 to-pink-100 p-6 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-4">For Direct Donations:</h4>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Phone className="text-purple-700 mr-3" size={20} />
                    <span className="text-gray-700">+91 9801133975</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="text-purple-700 mr-3" size={20} />
                    <span className="text-gray-700">rpeductionalwelfaretrusttrust@gmail.com</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
